/**
 * Host controls. SPEC.md §49, §20.2, §30, §50.
 *
 * Present, never loud — and now almost absent from the screen itself. Starting a
 * hand is a tap on a seat (§50) and a hand closes when the next one starts, so the
 * two buttons that used to live down here are gone: what remains inline is the
 * "entering for Sarah" banner, and everything else is behind the top bar's gear.
 * A player's screen is table and keypad whether they host or not. §20.2, §49
 *
 * "Complete hand" stays in the menu because closing a hand *without* starting
 * another is still a real thing to want — the last hand of the night. §4.4
 *
 * The lobby has its own host actions and an ended game is read-only, so this
 * renders for an active table only. §33, §32.1
 */

import { useEffect, useState, type ReactNode } from 'react';
import { truncateName, type PlayerView } from '@card-counter/shared';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Sheet } from '@/components/ui/Sheet';
import { Spinner } from '@/components/ui/Spinner';
import { useSession } from '@/hooks/useSession';
import {
  useActiveRound,
  useIsHost,
  useMyPlayerId,
  usePlayerById,
  usePlayersBySeat,
  useSessionInfo,
} from '@/stores/session-store';
import { useControllingPlayerId, useUiStore } from '@/stores/ui-store';
import { ShareCard } from '@/features/session/ShareCard';
import { EndGameConfirm } from './EndGameConfirm';

/** Which player the host is about to pick, and what for. §49 */
type PickerIntent = 'control' | 'seat' | 'remove' | 'dealer';
type MenuView = 'menu' | PickerIntent;

const PICKERS: Record<PickerIntent, { title: string; hint: string }> = {
  control: {
    title: 'Control player',
    hint: 'The keypad will enter points as whoever you pick.',
  },
  seat: {
    // Dragging on the ring is the primary gesture; this is the reachable twin. §9.3
    title: 'Rearrange players',
    hint: 'Pick someone to move, or press and hold their avatar on the table to drag them.',
  },
  remove: {
    title: 'Remove player',
    hint: 'Their points stay in the history and in the final score.',
  },
  /**
   * §9.6 — the one correction for a rotation that has re-indexed under the
   * table. The deal normally passes itself and needs no host at all; this is for
   * the night somebody joins halfway through and the button lands somewhere
   * nobody expects.
   */
  dealer: {
    title: 'Select dealer',
    hint: 'Give this hand’s deal to someone else. It carries on clockwise from them.',
  },
};

interface MenuRowProps {
  label: string;
  hint?: string;
  onClick(): void;
  disabled?: boolean;
  loading?: boolean;
  danger?: boolean;
}

function MenuRow({
  label,
  hint,
  onClick,
  disabled = false,
  loading = false,
  danger = false,
}: MenuRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className={[
        'flex min-h-tap w-full items-center gap-3 rounded-card border border-line bg-surface px-3 py-2 text-left',
        'transition-transform duration-150 ease-out-soft active:scale-[0.99]',
        'disabled:pointer-events-none disabled:opacity-45',
      ].join(' ')}
    >
      <span className="flex min-w-0 flex-1 flex-col">
        <span className={`truncate font-semibold ${danger ? 'text-negative' : 'text-ink'}`}>
          {label}
        </span>
        {hint === undefined ? null : (
          <span className="text-xs text-ink-muted">{hint}</span>
        )}
      </span>
      {loading ? <Spinner size="sm" label="Working…" /> : null}
    </button>
  );
}

interface PlayerPickerProps {
  players: readonly PlayerView[];
  onPick(playerId: string): void;
}

function PlayerPicker({ players, onPick }: PlayerPickerProps) {
  if (players.length === 0) {
    return <p className="text-sm text-ink-muted">There is nobody else at the table yet.</p>;
  }

  return (
    <ul className="flex flex-col gap-1.5">
      {players.map((player) => (
        <li key={player.id}>
          <button
            type="button"
            onClick={() => {
              onPick(player.id);
            }}
            className="flex min-h-tap w-full items-center gap-3 rounded-card border border-line bg-surface px-3 py-2 text-left transition-transform duration-150 ease-out-soft active:scale-[0.99]"
          >
            <Avatar avatarKey={player.avatarKey} className="size-8 shrink-0" />
            <span className="min-w-0 flex-1 truncate text-ink">{truncateName(player.name)}</span>
            {player.presence === 'offline' ? (
              <span className="text-xs text-ink-faint">Offline</span>
            ) : null}
          </button>
        </li>
      ))}
    </ul>
  );
}

export function HostControls() {
  const isHost = useIsHost();
  const session = useSessionInfo();
  const activeRound = useActiveRound();
  const players = usePlayersBySeat();
  const myPlayerId = useMyPlayerId();
  const controllingPlayerId = useControllingPlayerId();
  const controlled = usePlayerById(controllingPlayerId);

  const menuOpen = useUiStore((state) => state.hostMenuOpen);
  const closeMenu = useUiStore((state) => state.closeHostMenu);
  const openHistory = useUiStore((state) => state.openHistory);
  const openPlayerAdmin = useUiStore((state) => state.openPlayerAdmin);
  const setControllingPlayer = useUiStore((state) => state.setControllingPlayer);

  const { completeRound, setLocked, selectDealer, interactive } = useSession();

  const [view, setView] = useState<MenuView>('menu');
  const [lockPending, setLockPending] = useState(false);
  const [completePending, setCompletePending] = useState(false);
  const [endGameOpen, setEndGameOpen] = useState(false);

  // Reopening the menu always lands on the menu, never on the last picker. §37
  useEffect(() => {
    if (!menuOpen) setView('menu');
  }, [menuOpen]);

  if (!isHost || session?.status !== 'active') return null;

  const locked = session.locked;

  const toggleLock = async (): Promise<void> => {
    setLockPending(true);
    await setLocked(!locked);
    setLockPending(false);
  };

  const finishHand = async (): Promise<void> => {
    setCompletePending(true);
    const ok = await completeRound();
    setCompletePending(false);
    // The closed hand announces itself at every seat — the cards burst over the
    // roster — so there is nothing left to read in here. §4.4
    if (ok) closeMenu();
  };

  const pick = (playerId: string): void => {
    /**
     * Choosing a dealer is the one pick that *is* the action. §9.6
     *
     * Control, seat and removal all go on to the player's admin sheet, because
     * each of them needs a second decision — what to do, or a confirmation.
     * Naming a dealer needs neither: it moves a marker, it takes no points off
     * anybody, and the way back from a wrong pick is to pick again.
     */
    if (view === 'dealer') {
      // Naming the hand, not just the player: it may have closed itself between
      // the menu opening and this tap. §9.6, §4.3
      if (!activeRound) return;
      closeMenu();
      void selectDealer(activeRound.id, playerId);
      return;
    }
    // One admin surface for control, seat and removal, so the picker only has to
    // answer "who". §30, §31
    closeMenu();
    openPlayerAdmin(playerId);
  };

  const goto = (next: MenuView): void => {
    setView(next);
  };

  const pickable = (intent: PickerIntent): PlayerView[] => {
    /**
     * Only the players this hand is actually between may hold its button. §3.3
     *
     * Somebody who sat down after it opened is not among its frozen
     * participants, so the server refuses them — and offering a name the server
     * will reject is worse than not offering it. The host is included: dealing
     * is not something you do to somebody else.
     */
    if (intent === 'dealer') {
      const participants = new Set(activeRound?.participantIds ?? []);
      return players.filter((player) => participants.has(player.id));
    }
    // Controlling or removing yourself is not a thing; moving your own seat is. §9.3
    return intent === 'seat' ? players : players.filter((player) => player.id !== myPlayerId);
  };

  let sheetTitle = 'Host Controls';
  let sheetBody: ReactNode;

  if (view === 'menu') {
    sheetBody = (
      <div className="flex flex-col gap-1.5 pb-2">
        {controlled ? (
          <MenuRow
            label={`Stop controlling ${truncateName(controlled.name)}`}
            hint="Go back to entering your own points."
            onClick={() => {
              setControllingPlayer(null);
              closeMenu();
            }}
          />
        ) : null}

        <MenuRow
          label="Complete Hand"
          // A hand ends itself the moment the table has told it enough, so this
          // is for the last hand of the night — or one started by mistake. Not a
          // host power any more, but this is still where it is reached
          // from. §4.4, §4.3, §5.1
          hint={
            activeRound
              ? 'Close it without dealing another. Hands otherwise close themselves.'
              : 'No hand is running right now.'
          }
          disabled={activeRound === null || !interactive}
          loading={completePending}
          onClick={() => {
            void finishHand();
          }}
        />

        {/*
          Deleting is done in history rather than here, because the hand that
          needs deleting is not reliably the last one and only history can say
          which is which. This is the signpost. §12.5, §13.2
        */}
        <MenuRow
          label="Delete a Hand"
          hint="Cancels its points so the table can enter it again. Opens history."
          danger
          onClick={() => {
            closeMenu();
            openHistory();
          }}
        />

        <MenuRow
          label={locked ? 'Unlock Table' : 'Lock Table'}
          // §27.1 — locking stops new joins only; players already here still reconnect.
          hint={locked ? 'No new players can join right now.' : 'Anyone with the code can join.'}
          loading={lockPending}
          disabled={!interactive}
          onClick={() => {
            void toggleLock();
          }}
        />

        <MenuRow
          label="Control Player"
          hint="Report a loss on someone else's behalf."
          onClick={() => {
            goto('control');
          }}
        />

        {/* Collapsed by default — the code above it is what people read out. §6.1 */}
        <ShareCard />

        <MenuRow
          label="Rearrange Players"
          hint="Move someone to a different seat."
          onClick={() => {
            goto('seat');
          }}
        />

        {/*
          §9.6 — the deal passes itself every hand, so this exists for the one
          case it cannot handle: somebody joining mid-game re-indexes the
          rotation, and the table wants the button back where they expect it.
          There has to be a hand out for it to belong to.
        */}
        <MenuRow
          label="Select Dealer"
          hint={
            activeRound
              ? 'Give this hand’s deal to someone else.'
              : 'No hand is running right now.'
          }
          disabled={activeRound === null || !interactive}
          onClick={() => {
            goto('dealer');
          }}
        />

        <MenuRow
          label="Remove Player"
          hint="Takes them off the table without touching the history."
          danger
          onClick={() => {
            goto('remove');
          }}
        />

        <MenuRow
          label="End Game"
          hint="Show the final scores and stop taking points."
          danger
          onClick={() => {
            closeMenu();
            setEndGameOpen(true);
          }}
        />
      </div>
    );
  } else {
    const picker = PICKERS[view];
    sheetTitle = picker.title;
    sheetBody = (
      <div className="flex flex-col gap-3 pb-2">
        <p className="text-sm text-ink-muted">{picker.hint}</p>
        <PlayerPicker players={pickable(view)} onPick={pick} />
        <Button
          variant="ghost"
          fullWidth
          onClick={() => {
            goto('menu');
          }}
        >
          Back
        </Button>
      </div>
    );
  }

  return (
    <>
      {/*
        §30 requires the acting-for state to be visible with an obvious way out.
        During a hand the keypad carries that banner, right where the keys it
        changes are; between hands there is no keypad to qualify, so it belongs
        here — and only here, because two banners would cost a row the one-viewport
        screen doesn't have. §18.2
      */}
      {activeRound === null && controlled ? (
        <div
          role="status"
          className="flex min-h-tap shrink-0 items-center gap-2 rounded-card border border-accent bg-accent-soft px-3 py-1.5"
        >
          <Avatar avatarKey={controlled.avatarKey} className="size-7 shrink-0" />
          <span className="min-w-0 flex-1 truncate text-sm text-ink">
            Entering for <strong className="font-semibold">{truncateName(controlled.name)}</strong>
          </span>
          <button
            type="button"
            onClick={() => {
              setControllingPlayer(null);
            }}
            className="shrink-0 rounded-control px-2 py-1 text-sm font-semibold tracking-wide text-accent uppercase active:opacity-70"
          >
            Done
          </button>
        </div>
      ) : null}

      <Sheet open={menuOpen} onClose={closeMenu} title={sheetTitle}>
        {sheetBody}
      </Sheet>

      <EndGameConfirm
        open={endGameOpen}
        onClose={() => {
          setEndGameOpen(false);
        }}
      />
    </>
  );
}
